import type { ProseDensity } from './response-scale';

/**
 * Vertical rhythm BETWEEN the components of a conversation, one level up from
 * the prose rhythm in `response-prose`.
 *
 * ## One ladder, four rungs
 *
 * Every vertical gap in a thread separates one of four things, and no surface
 * is allowed to pick its own number for them:
 *
 * | rung               | separates                                     |
 * | ------------------ | --------------------------------------------- |
 * | within-block       | paragraphs, lists, headings in one block       |
 * | `withinAnswer`     | the facets of one response                     |
 * | `questionToAnswer` | a question from the answer it produced         |
 * | `turnToTurn`       | one exchange from the next                     |
 *
 * The first rung is the prose scale and lives in `response-prose`; the other
 * three are here.
 *
 * ## The numbers are derived, not picked
 *
 * Every rung is a multiple of the BODY LINE BOX — the one vertical measure the
 * content already fixes. Body is 16px at `leading-normal` on a reading surface
 * and 14px in a panel, so the line box is 24px and 21px respectively, and the
 * rungs are 1, 1.5 and 2 of it, rounded to the Tailwind scale:
 *
 * |                    | comfortable | compact   |
 * | ------------------ | ----------- | --------- |
 * | `withinAnswer`     | 24 (1×)     | 20 (1×)   |
 * | `questionToAnswer` | 44 (1.83×)  | 32 (1.5×) |
 * | `turnToTurn`       | 52 (2.17×)  | 40 (2×)   |
 *
 * Comfortable started on the same 1 : 1.5 : 2 and was lifted when its section
 * heading tier rose — see `TURN_CLASS`.
 *
 * That is what keeps the ratio constant across densities while the absolute
 * values shrink, and it is why a future change here should move the multiplier
 * rather than the pixel count. Hand-picking is how the two surfaces drifted to
 * `gap-4` and `gap-5` for the same relationship in the first place.
 *
 * The rungs are only legible against each other, which is why the invariants
 * that bind them are pinned in `response-thread.test.ts` rather than left to
 * judgement:
 *
 *   1. a section heading never out-separates `questionToAnswer`
 *   2. nothing inside an answer out-separates `turnToTurn`
 *   3. inside an answer the prose rungs stay distinguishable
 *   4. compact is tighter than comfortable at every rung
 *
 * Invariant 1 is the one that used to be violated in both directions: a
 * comfortable `h1` opened 64px while a whole new exchange opened 48px, so a
 * heading mid-answer read as a bigger break than a new question. The prose
 * scale was lowered to satisfy it, not the thread scale raised.
 *
 * ## `space-y-*`, never `gap-*`
 *
 * These are not interchangeable here. `questionToAnswer` is expressed as a
 * `margin-top` on whatever follows the question (see below), which REPLACES a
 * `space-y-*` but STACKS ON TOP OF a flex `gap-*`. A container on `gap-4` that
 * took the same override would render 16+24=40px where 24px was meant. So
 * every thread container normalizes onto `space-y-*` and this module — not the
 * consumer — owns that choice.
 *
 * ## The question-to-answer rung is relational, not a margin
 *
 * Two families render a question (`UserMessage` in Evidence chat, the bubble in
 * the Work timeline) and between them four shapes. None can own this gap: it
 * belongs to the boundary, not to either side. Expressing it as a bottom margin
 * is what produced the bug this replaces — the branded shape carried `mb-6` and
 * the bubble carried nothing, so the SAME relationship rendered at 40px or 16px
 * depending on how many characters the user typed.
 *
 * Every shape already marks itself with `data-user-message`, so one
 * adjacent-sibling rule on the turn container states it once for all of them,
 * and anything that later renders after a question inherits it for free.
 *
 * ## Why it lives here and not in `src/components/chat`
 *
 * Both families import these tokens, and the Work family lives in
 * `src/features/projects` + `packages/agents`. Chat is quarantined from the
 * product domains in both directions, so a new `features -> chat` edge would
 * fail `pnpm migration:check`. Sitting beside `response-prose` is also where it
 * belongs on the merits: the two rhythms share `ProseDensity`.
 *
 * Spelled as literals, for the same reason as the prose selectors: Tailwind
 * generates utilities by scanning source text, so a class assembled at runtime
 * produces no CSS at all.
 */

/**
 * The rhythm inside one exchange: `space-y-*` is the within-answer rung, and
 * the `[&>[data-user-message]+*]` override promotes whichever block follows the
 * question to the question-to-answer rung.
 *
 * `!` on the override because `space-y-*` compiles to
 * `> :not([hidden]) ~ :not([hidden])` at specificity (0,3,0) — the same reason
 * the prose block margins carry it.
 */
const TURN_CLASS = {
  // 24px between the facets of an answer, 44px after the question.
  //
  // This rung exists to stay strictly above the section-heading rung, so that a
  // heading mid-answer never breaks as hard as the boundary between the reader's
  // question and the answer. Full-screen wanted more air between `h2` sections
  // than 36px, and the section tier can only rise if this rises with it, so the
  // whole comfortable ladder was lifted a rung: 24 / 40 / 44 / 52, or
  // 1 : 1.67 : 1.83 : 2.17 against the 24px line box. That deliberately widened
  // the pinned ratio bands — the proportional tidiness of 1 : 1.5 : 2 was worth
  // less than legible section breaks on the primary reading surface.
  comfortable: 'space-y-6 [&>[data-user-message]+*]:!mt-11',
  // 20px / 32px. Tighter, but the ladder is preserved, so a narrow panel still
  // reads the question as separate from the answer.
  compact: 'space-y-5 [&>[data-user-message]+*]:!mt-8',
} as const satisfies Record<ProseDensity, string>;

/**
 * The within-answer rung alone, for a container that holds only the facets of
 * one answer and so has no question boundary to express. Same two values as the
 * `space-y-*` in `TURN_CLASS`, written out because Tailwind cannot see a class
 * that is derived rather than spelled.
 */
const ANSWER_CLASS = {
  comfortable: 'space-y-6',
  compact: 'space-y-5',
} as const satisfies Record<ProseDensity, string>;

/** 52px / 40px between one exchange and the next — the largest gap in a thread,
 * and the reason invariant 2 exists.
 *
 * Both chat families pin the newest question to the top of the viewport
 * (`minHeight: calc(100dvh - …)` in `ChatTimeline`, `latestTurnMinHeight` in
 * `ProjectChat`), so in the settled state this gap is usually scrolled off. It
 * earns its keep when reading back through history, not on send — which is why
 * it stays near 2 line-boxes rather than the 3 it was first drafted at.
 *
 * 52px is spelled as an explicit length because it is on the 4px grid but NOT on
 * Tailwind's spacing scale: there is no `space-y-13`, and `space-y-52` is 13rem
 * (208px), which would silently ship a gap four times too large.
 */
const COLUMN_CLASS = {
  comfortable: 'space-y-[3.25rem]',
  compact: 'space-y-10',
} as const satisfies Record<ProseDensity, string>;

/**
 * How wide a question bubble may get. The only horizontal rung here, and it
 * takes two bounds because neither alone is sufficient.
 *
 * A percentage is what makes the bubble read as right-aligned. Both families
 * already wrap the bubble in `justify-end`, so alignment was never the bug —
 * the bug is that an ABSOLUTE cap cannot leave a gutter. Evidence capped at
 * `max-w-md` (448px) inside a 293px sidebar, so the cap never engaged, the
 * bubble filled the column edge to edge and the alignment became invisible.
 * 85% guarantees a gutter at every width. Messaging apps sit nearer 75%, but
 * they are full-window conversations; at 293px that would spend 73px on empty
 * space and force the wrapping that 85% avoids while still showing 44px of it.
 *
 * An absolute cap is what keeps the line readable, and is why the percentage
 * cannot stand alone: 85% of a wide column is a line far past the 45–75
 * character range. The two values below are the ones already in the tree —
 * 36rem at the comfortable 16px body and 28rem at the compact 14px are both
 * ≈72 characters — so this adds the missing bound rather than re-tuning the
 * present one.
 *
 * `min()` resolves to whichever is smaller at the current width, which is the
 * whole point: one declaration, no breakpoint, correct at every column size.
 */
const QUESTION_WIDTH_CLASS = {
  comfortable: 'max-w-[min(85%,36rem)]',
  compact: 'max-w-[min(85%,28rem)]',
} as const satisfies Record<ProseDensity, string>;

/**
 * Rhythm within one exchange, for the element that wraps a single turn's blocks
 * as direct children. The adjacent-sibling override only resolves if the
 * question and the answer really are siblings here — in the Work family that is
 * `AgentTimeline`'s own container, not the wrapper around it.
 */
export const responseTurnClass = (density: ProseDensity): string => TURN_CLASS[density];

/**
 * Rhythm between the facets of one answer, for a container that holds no
 * question — an embedded Evidence answer inside a Work timeline, where the
 * search group, the prose and the library blocks are all facets of one
 * response.
 */
export const responseAnswerGapClass = (density: ProseDensity): string =>
  ANSWER_CLASS[density];

/**
 * Rhythm between exchanges, for the scroll column whose direct children are
 * whole turns.
 */
export const responseColumnClass = (density: ProseDensity): string =>
  COLUMN_CLASS[density];

/**
 * Width cap for the question bubble itself — the inner element that carries the
 * background, not the `justify-end` wrapper around it.
 */
export const responseQuestionWidthClass = (density: ProseDensity): string =>
  QUESTION_WIDTH_CLASS[density];
